/**
 * AR abstraction.
 *
 * There is no single AR API that covers the phones this needs to run on.
 * Android Chrome has WebXR with real plane/hit-test tracking. iOS Safari has
 * none of it and shows no sign of shipping it, and iOS is far too large a
 * share of the audience to drop.
 *
 * So there are three backends behind one interface:
 *
 *   `webxr`  — real `immersive-ar` session with hit-testing against detected
 *              surfaces. Six degrees of freedom; the player can walk around
 *              their garden.
 *
 *   `camera` — rear camera as a video backdrop, device orientation driving
 *              the camera rotation, and a virtual floor at a fixed drop below
 *              eye level. Three degrees of freedom: the player can look
 *              around but not walk. Honest about its limits, and it works
 *              everywhere including desktop with a webcam.
 *
 *   `preview` — no camera at all. The garden sits on a plinth against a flat
 *              backdrop and the player orbits it by dragging. This is what
 *              desktops get, and what in-app browsers that refuse camera
 *              access (WhatsApp, LinkedIn, X) fall back to. Shipping a dead
 *              end to those users would waste most of the link-shares this
 *              is going to get.
 *
 * The game layer above this never needs to know which one is running.
 */

import * as THREE from 'three';

import { ease, neutralSample, sampleFrom, type LightSample } from './xrLighting';

export type ARMode = 'webxr' | 'camera' | 'preview';

export interface HitResult {
  /** World-space point on the surface. */
  position: THREE.Vector3;
  /** Surface orientation, already converted to a quaternion. */
  quaternion: THREE.Quaternion;
}

export interface ARBackend {
  readonly mode: ARMode;
  start(): Promise<void>;
  stop(): void;
  /** Called once per frame before rendering. `frame` is only set under WebXR. */
  update(frame: XRFrame | null): void;
  /** Current placement candidate under the screen centre, or null. */
  hitTest(): HitResult | null;
  /** True once tracking is good enough to place something. */
  readonly ready: boolean;
  /**
   * Tell the backend how tall the garden currently is, in metres, so it can
   * frame it. Only `preview` composes a shot; in real AR the player's own
   * head does the framing and this is ignored.
   */
  setContentHeight?(metres: number, plotRadius?: number): void;
  /**
   * The live backdrop behind the garden, if this backend has one that can be
   * read back into a canvas.
   *
   * Only `camera` does. `preview` has no backdrop at all, and under WebXR the
   * feed is composited by the runtime and is not reachable from the page —
   * which is why an AR share from a headset cannot contain the room.
   */
  backdropSource?(): CanvasImageSource | null;
  /**
   * Real-world lighting measured this frame, or null when the runtime cannot
   * measure it. Only `webxr` ever can.
   */
  lightSample?(): LightSample | null;
  /**
   * What the runtime actually granted, for diagnosis on a real device.
   *
   * Every capability here is optional and silently absent on most hardware,
   * so without this the only way to tell whether occlusion is on is to look
   * at the screen and guess. `window.__garden.arFeatures` surfaces it.
   */
  features?(): string[];
}

/** Assumed distance from the phone to the floor when we cannot measure it. */
const ASSUMED_EYE_HEIGHT = 1.45;

/* ------------------------------------------------------------------ *
 * WebXR backend
 * ------------------------------------------------------------------ */

class WebXRBackend implements ARBackend {
  readonly mode = 'webxr' as const;
  ready = false;

  private session: XRSession | null = null;
  private hitTestSource: XRHitTestSource | null = null;
  private refSpace: XRReferenceSpace | null = null;
  private lastHit: HitResult | null = null;
  private lightProbe: XRLightProbe | null = null;
  /** Eased, so a jittering estimate does not make the garden flicker. */
  private light: LightSample = neutralSample();
  private lastLightAt = 0;
  private readonly matrix = new THREE.Matrix4();
  private readonly scratchPos = new THREE.Vector3();
  private readonly scratchQuat = new THREE.Quaternion();
  private readonly scratchScale = new THREE.Vector3();

  private renderer: THREE.WebGLRenderer;
  private overlayRoot: HTMLElement;
  private onEnd: () => void;

  constructor(
    renderer: THREE.WebGLRenderer,
    overlayRoot: HTMLElement,
    onEnd: () => void,
  ) {
    this.renderer = renderer;
    this.overlayRoot = overlayRoot;
    this.onEnd = onEnd;
  }

  async start(): Promise<void> {
    /*
     * Everything past `hit-test` and `local-floor` is optional on purpose. A
     * device that grants none of it gets exactly the app that has been tested
     * on a Samsung S24 Ultra and works; a device that grants them gets a
     * garden that is occluded by real furniture and lit by the real sun.
     *
     * `depth-sensing` is what buys the occlusion, and three.js does the whole
     * pass itself — it builds a full-screen mesh that writes `gl_FragDepth`
     * from the runtime's depth texture and draws it before everything else,
     * so the depth buffer already holds the room by the time the garden is
     * drawn. The only requirement on us is asking for it correctly:
     * `gpu-optimized` is listed first because three ignores the CPU path
     * entirely (`session.depthUsage == 'gpu-optimized'` is a hard condition
     * in WebXRManager), so a runtime that hands back `cpu-optimized` gives us
     * a feature that is enabled and does nothing.
     */
    const session = await navigator.xr!.requestSession('immersive-ar', {
      requiredFeatures: ['hit-test', 'local-floor'],
      optionalFeatures: ['dom-overlay', 'light-estimation', 'depth-sensing'],
      domOverlay: { root: this.overlayRoot },
      depthSensing: {
        usagePreference: ['gpu-optimized', 'cpu-optimized'],
        dataFormatPreference: ['luminance-alpha', 'float32'],
      },
    } as XRSessionInit);
    this.session = session;

    session.addEventListener('end', () => {
      this.hitTestSource = null;
      this.lightProbe = null;
      this.light = neutralSample();
      this.session = null;
      this.ready = false;
      this.onEnd();
    });

    await this.renderer.xr.setSession(session);

    this.refSpace = await session.requestReferenceSpace('local-floor');
    const viewerSpace = await session.requestReferenceSpace('viewer');
    this.hitTestSource =
      (await session.requestHitTestSource!({ space: viewerSpace })) ?? null;

    /*
     * The probe is requested rather than assumed: `light-estimation` was in
     * the optional list for months before this and nothing ever read it, so
     * the feature was granted and wasted. Failing to get one is ordinary —
     * plenty of runtimes advertise the feature and refuse the probe — so it
     * is caught and the garden keeps its authored lighting.
     */
    if (session.enabledFeatures?.includes('light-estimation')) {
      try {
        this.lightProbe = (await session.requestLightProbe?.()) ?? null;
      } catch {
        this.lightProbe = null;
      }
    }
  }

  /** What the runtime granted. Read on a device via `window.__garden.arFeatures`. */
  features(): string[] {
    const session = this.session;
    if (!session) return [];
    const granted = [...(session.enabledFeatures ?? [])];
    // `depth-sensing` in the list is not the same as occlusion working — see
    // the note in `start()`. Say which one is true.
    if (granted.includes('depth-sensing')) {
      granted.push(`depth-usage:${session.depthUsage ?? 'unknown'}`);
      granted.push(`depth-format:${session.depthDataFormat ?? 'unknown'}`);
      granted.push(
        this.renderer.xr.hasDepthSensing?.() ? 'occlusion:active' : 'occlusion:inactive',
      );
    }
    if (this.lightProbe) granted.push('light-probe:active');
    return granted;
  }

  lightSample(): LightSample | null {
    return this.lightProbe ? this.light : null;
  }

  /**
   * Read the probe and ease toward it.
   *
   * The estimate is re-measured every frame and moves about; applied directly
   * the garden strobes. Easing here rather than at the call site keeps the
   * whole of `App` reading a settled number.
   */
  private sampleLight(frame: XRFrame): void {
    if (!this.lightProbe) return;

    const now = performance.now();
    const dt = this.lastLightAt === 0 ? 1 / 60 : Math.min(0.1, (now - this.lastLightAt) / 1000);
    this.lastLightAt = now;

    let target: LightSample;
    try {
      target = sampleFrom(frame.getLightEstimate?.(this.lightProbe));
    } catch {
      // A probe can start throwing mid-session if tracking is lost.
      return;
    }

    this.light = {
      // The direction is not eased: it is already smooth, and interpolating
      // two unit vectors component-wise shortens the result on the way past.
      direction: target.direction ?? this.light.direction,
      sun: ease(this.light.sun, target.sun, dt),
      ambient: ease(this.light.ambient, target.ambient, dt),
      colour: [
        ease(this.light.colour[0], target.colour[0], dt),
        ease(this.light.colour[1], target.colour[1], dt),
        ease(this.light.colour[2], target.colour[2], dt),
      ],
    };
  }

  stop(): void {
    void this.session?.end();
  }

  update(frame: XRFrame | null): void {
    if (!frame || !this.refSpace) return;

    this.sampleLight(frame);

    if (!this.hitTestSource) return;

    const results = frame.getHitTestResults(this.hitTestSource);
    if (results.length === 0) {
      this.lastHit = null;
      return;
    }

    const pose = results[0].getPose(this.refSpace);
    if (!pose) {
      this.lastHit = null;
      return;
    }

    this.matrix.fromArray(pose.transform.matrix);
    this.matrix.decompose(this.scratchPos, this.scratchQuat, this.scratchScale);
    this.lastHit = {
      position: this.scratchPos.clone(),
      quaternion: this.scratchQuat.clone(),
    };
    this.ready = true;
  }

  hitTest(): HitResult | null {
    return this.lastHit;
  }
}

/* ------------------------------------------------------------------ *
 * Camera + gyroscope backend
 * ------------------------------------------------------------------ */

class CameraBackend implements ARBackend {
  readonly mode = 'camera' as const;
  ready = false;

  private video: HTMLVideoElement | null = null;
  private stream: MediaStream | null = null;
  private orientation: { alpha: number; beta: number; gamma: number } | null = null;
  private screenAngle = 0;
  private readonly floorPlane = new THREE.Plane(
    new THREE.Vector3(0, 1, 0),
    ASSUMED_EYE_HEIGHT,
  );
  private readonly raycaster = new THREE.Raycaster();
  private readonly centre = new THREE.Vector2(0, 0);
  private readonly hitPoint = new THREE.Vector3();
  private readonly flatQuat = new THREE.Quaternion();
  private onOrient = (e: DeviceOrientationEvent) => {
    if (e.alpha === null || e.beta === null || e.gamma === null) return;
    this.orientation = { alpha: e.alpha, beta: e.beta, gamma: e.gamma };
    this.ready = true;
  };

  private camera: THREE.PerspectiveCamera;
  private container: HTMLElement;

  constructor(camera: THREE.PerspectiveCamera, container: HTMLElement) {
    this.camera = camera;
    this.container = container;
  }

  /**
   * The camera feed, once it has frames.
   *
   * `.ar-video` is laid out with `object-fit: cover` over the whole stage, so
   * a caller compositing this behind the render must cover-fit it into the
   * canvas rect to reproduce what the player is actually looking at.
   */
  backdropSource(): CanvasImageSource | null {
    // HAVE_CURRENT_DATA. Drawing a video with no frame yet paints nothing and
    // would silently give back a card with a hole in it.
    if (!this.video || this.video.readyState < 2) return null;
    return this.video;
  }

  async start(): Promise<void> {
    // Rear camera. `exact` would throw on desktop webcams, so this is a
    // preference rather than a demand.
    this.stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: 'environment' },
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
      audio: false,
    });

    const video = document.createElement('video');
    video.setAttribute('playsinline', '');
    video.setAttribute('muted', '');
    video.muted = true;
    video.autoplay = true;
    video.srcObject = this.stream;
    video.className = 'ar-video';
    this.container.prepend(video);
    await video.play();
    this.video = video;

    await this.requestOrientationPermission();

    window.addEventListener('deviceorientation', this.onOrient, true);
    window.addEventListener('orientationchange', this.readScreenAngle);
    this.readScreenAngle();

    // Desktop and any device without a gyro still needs to be usable, so
    // don't gate readiness on an orientation event that may never arrive.
    window.setTimeout(() => {
      this.ready = true;
    }, 600);
  }

  private readScreenAngle = (): void => {
    const angle = (screen.orientation?.angle ?? (window as any).orientation ?? 0) as number;
    this.screenAngle = THREE.MathUtils.degToRad(angle);
  };

  /** iOS 13+ requires an explicit, gesture-initiated grant for the gyro. */
  private async requestOrientationPermission(): Promise<void> {
    const anyDOE = DeviceOrientationEvent as any;
    if (typeof anyDOE?.requestPermission === 'function') {
      try {
        await anyDOE.requestPermission();
      } catch {
        // Denied or dismissed. The scene still renders; it just won't track
        // head rotation. Better than refusing to start at all.
      }
    }
  }

  stop(): void {
    window.removeEventListener('deviceorientation', this.onOrient, true);
    window.removeEventListener('orientationchange', this.readScreenAngle);
    this.stream?.getTracks().forEach((t) => t.stop());
    this.video?.remove();
    this.video = null;
    this.stream = null;
    this.ready = false;
  }

  update(): void {
    if (!this.orientation) return;
    const { alpha, beta, gamma } = this.orientation;
    setQuaternionFromDeviceOrientation(
      this.camera.quaternion,
      THREE.MathUtils.degToRad(alpha),
      THREE.MathUtils.degToRad(beta),
      THREE.MathUtils.degToRad(gamma),
      this.screenAngle,
    );
  }

  hitTest(): HitResult | null {
    if (!this.ready) return null;
    // Ray straight out of the screen centre onto the assumed floor.
    this.raycaster.setFromCamera(this.centre, this.camera);
    const hit = this.raycaster.ray.intersectPlane(this.floorPlane, this.hitPoint);
    if (!hit) return null;

    // Refuse absurd placements — pointing at the horizon gives a hit
    // kilometres away that would render as a speck.
    const dist = hit.distanceTo(this.camera.position);
    if (dist > 6 || dist < 0.25) return null;

    return { position: hit.clone(), quaternion: this.flatQuat };
  }
}

/**
 * Convert device orientation Euler angles to a camera quaternion.
 *
 * The device frame has Z out of the screen and Y up the device; the camera
 * looks down -Z. The -90° X rotation reconciles the two, and the trailing
 * screen-angle term keeps the horizon level when the phone is rotated to
 * landscape.
 */
const ZEE = new THREE.Vector3(0, 0, 1);
const EULER = new THREE.Euler();
const Q0 = new THREE.Quaternion();
const Q1 = new THREE.Quaternion(-Math.sqrt(0.5), 0, 0, Math.sqrt(0.5));

function setQuaternionFromDeviceOrientation(
  target: THREE.Quaternion,
  alpha: number,
  beta: number,
  gamma: number,
  screenAngle: number,
): void {
  EULER.set(beta, alpha, -gamma, 'YXZ');
  target.setFromEuler(EULER);
  target.multiply(Q1);
  target.multiply(Q0.setFromAxisAngle(ZEE, -screenAngle));
}

/* ------------------------------------------------------------------ *
 * Preview backend — no camera, orbit the garden on a plinth
 * ------------------------------------------------------------------ */

class PreviewBackend implements ARBackend {
  readonly mode = 'preview' as const;
  ready = true;

  private camera: THREE.PerspectiveCamera;
  private container: HTMLElement;

  // Spherical orbit around the plot.
  private azimuth = 0.5;
  /* Lowered from 0.55. `preview` is the one mode with a real horizon behind
     the garden, and at 0.55 the camera looked down steeply enough that the
     desert and the skyline sat off the top of the frame unless the player
     happened to drag downward. */
  private elevation = 0.42;
  /* Far enough out to frame a full plot of mature date palms (~1.15 m) with
     headroom. At 1.5 the camera sat inside the canopy. */
  private radius = 2.4;
  private targetAzimuth = 0.5;
  private targetElevation = 0.42;
  private targetRadius = 2.4;

  private dragging = false;
  private lastX = 0;
  private lastY = 0;
  // Aim at the canopy mass rather than the soil, so the framing is not
  // bottom-heavy before anything has grown.
  private readonly focus = new THREE.Vector3(0, 0.12, 0);
  private readonly flatQuat = new THREE.Quaternion();

  constructor(camera: THREE.PerspectiveCamera, container: HTMLElement) {
    this.camera = camera;
    this.container = container;
  }

  async start(): Promise<void> {
    this.container.addEventListener('pointerdown', this.onDown);
    window.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp);
    window.addEventListener('pointercancel', this.onUp);
    this.container.addEventListener('wheel', this.onWheel, { passive: false });
    this.applyCamera();
  }

  stop(): void {
    this.container.removeEventListener('pointerdown', this.onDown);
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
    window.removeEventListener('pointercancel', this.onUp);
    this.container.removeEventListener('wheel', this.onWheel);
  }

  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    this.targetRadius = THREE.MathUtils.clamp(
      this.targetRadius + e.deltaY * 0.0022,
      1.4,
      5,
    );
  };

  private onDown = (e: PointerEvent): void => {
    // Let the overlay keep its own gestures; only bare stage drags orbit.
    if ((e.target as HTMLElement | null)?.closest('button, .species-rail')) return;
    this.dragging = true;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
  };

  private onMove = (e: PointerEvent): void => {
    if (!this.dragging) return;
    const dx = e.clientX - this.lastX;
    const dy = e.clientY - this.lastY;
    this.lastX = e.clientX;
    this.lastY = e.clientY;

    this.targetAzimuth -= dx * 0.006;
    this.targetElevation = THREE.MathUtils.clamp(
      this.targetElevation - dy * 0.005,
      0.06, // never go under the plot
      1.35, // nor straight down onto it
    );
  };

  private onUp = (): void => {
    this.dragging = false;
  };

  private applyCamera(): void {
    const cosE = Math.cos(this.elevation);
    this.camera.position.set(
      this.focus.x + Math.sin(this.azimuth) * cosE * this.radius,
      this.focus.y + Math.sin(this.elevation) * this.radius,
      this.focus.z + Math.cos(this.azimuth) * cosE * this.radius,
    );
    this.camera.lookAt(this.focus);
  }

  update(): void {
    // Ease toward the drag target so flicks glide instead of snapping.
    this.azimuth += (this.targetAzimuth - this.azimuth) * 0.12;
    this.elevation += (this.targetElevation - this.elevation) * 0.12;
    this.radius += (this.targetRadius - this.radius) * 0.12;
    // A slow idle drift when untouched, so the scene never looks frozen.
    if (!this.dragging) this.targetAzimuth += 0.0009;
    this.applyCamera();
  }

  hitTest(): HitResult | null {
    // The plot always goes at the origin here; there is no world to search.
    return { position: new THREE.Vector3(0, 0, 0), quaternion: this.flatQuat };
  }

  /**
   * Reframe as the garden grows. A bare plot wants a close, low camera; a
   * plot of mature date palms wants to be further back and aimed higher.
   * Both targets are eased in `update`, so the recompose is a slow drift
   * rather than a jump the moment something matures.
   */
  setContentHeight(metres: number, plotRadius = 0.7): void {
    const h = THREE.MathUtils.clamp(metres, 0, 0.6);
    this.focus.y = 0.08 + h * 0.5;
    /*
     * The floor is set by the plot's own width: any closer and a portrait
     * phone crops the terrace before anything has even grown. It scales with
     * the plot because the garden now opens out to more than twice its
     * starting radius — a frame composed for a 0.7 m terrace cuts the wetland
     * clean off.
     */
    const floor = 2.3 + Math.max(0, plotRadius - 0.7) * 2;
    this.targetRadius = THREE.MathUtils.clamp(floor + h * 1.5, floor, floor * 1.48);
  }
}

/* ------------------------------------------------------------------ *
 * Capability detection and factory
 * ------------------------------------------------------------------ */

export interface ARCapabilities {
  webxr: boolean;
  camera: boolean;
  /** The backend we intend to start. `preview` is always available. */
  preferred: ARMode;
  /**
   * Why we are not offering real AR, phrased for the player. Null when we
   * are. This is never fatal any more — `preview` always works — so it is a
   * caveat to show, not an error.
   */
  degradedReason: string | null;
}

export async function detectCapabilities(): Promise<ARCapabilities> {
  const secure = window.isSecureContext;
  const camera = secure && !!navigator.mediaDevices?.getUserMedia;

  let webxr = false;
  if (secure && navigator.xr?.isSessionSupported) {
    try {
      webxr = await navigator.xr.isSessionSupported('immersive-ar');
    } catch {
      webxr = false;
    }
  }

  // An explicit `?mode=` wins, which makes the non-AR path demoable on any
  // machine and gives support something to ask people to try.
  const forced = new URLSearchParams(location.search).get('mode');
  if (forced === 'preview' || forced === 'camera' || forced === 'webxr') {
    return { webxr, camera, preferred: forced, degradedReason: null };
  }

  let degradedReason: string | null = null;
  let preferred: ARMode = 'preview';

  if (webxr) {
    preferred = 'webxr';
  } else if (camera) {
    preferred = 'camera';
  } else if (!secure) {
    degradedReason =
      'Camera access needs a secure connection, so AR is unavailable here.';
  } else {
    degradedReason = 'This browser will not give a web page camera access.';
  }

  return { webxr, camera, preferred, degradedReason };
}

export function createBackend(opts: {
  mode: ARMode;
  renderer: THREE.WebGLRenderer;
  camera: THREE.PerspectiveCamera;
  container: HTMLElement;
  overlayRoot: HTMLElement;
  onEnd: () => void;
}): ARBackend {
  switch (opts.mode) {
    case 'webxr':
      return new WebXRBackend(opts.renderer, opts.overlayRoot, opts.onEnd);
    case 'camera':
      return new CameraBackend(opts.camera, opts.container);
    case 'preview':
      return new PreviewBackend(opts.camera, opts.container);
  }
}
